/**
 * Etapa 2 do wizard — Informacoes basicas + Quem define o valor.
 *
 * Layout 2 colunas (lg+):
 *  - Esquerda: nome + codigo + cards "Eu defino" / "Cliente escolhe" + callout
 *  - Direita: preview iPhone reativo (sticky)
 *
 * Os INPUTS de valor (valor fixo OU faixa min/max/incremento) ficam na
 * Etapa 3 (Regras da cobranca), dentro da secao "1. Valor da cobranca".
 */

import { useTranslation } from "react-i18next";
import type { Dispatch, SetStateAction } from "react";
import type { PlanFormState } from "../types";
import { ChargePlanMobilePreview } from "../components/ChargePlanMobilePreview";
import { Field } from "../components/Field";

interface Props {
  state: PlanFormState;
  setState: Dispatch<SetStateAction<PlanFormState>>;
}

export function Step1Modality({ state, setState }: Props) {
  const { t } = useTranslation();

  return (
    <div className="grid grid-cols-1 gap-8 lg:grid-cols-[1fr_360px]">
      <div className="flex flex-col gap-6">
        <header>
          <h1 className="text-2xl font-bold text-foreground">
            {t("charge_plans.step1_basic.title")}
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {t("charge_plans.step1_basic.subtitle")}
          </p>
        </header>

        <div className="grid grid-cols-1 gap-4 rounded-2xl border border-border bg-card p-5 md:grid-cols-2">
          <Field
            label={t("charge_plans.step1.name.label")}
            hint={t("charge_plans.step1.name.hint")}
          >
            <input
              type="text"
              value={state.nome}
              onChange={(e) =>
                setState({ ...state, nome: e.target.value.slice(0, 120) })
              }
              maxLength={120}
              placeholder={t("charge_plans.step1.name.placeholder")}
              className="h-11 w-full rounded-lg border border-input bg-card px-3 text-sm text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            />
          </Field>

          <Field
            label={t("charge_plans.step1.code.label")}
            hint={t("charge_plans.step1.code.hint")}
          >
            {/* Codigo eh gerado no backend ao salvar (createChargePlan).
                Em criacao o input fica desabilitado mostrando "Gerado
                automaticamente ao salvar"; em edicao mostra o codigo ja
                gerado, ainda nao-editavel (alterar pode quebrar codigos
                ja distribuidos). */}
            <input
              type="text"
              value={state.codigo}
              readOnly
              disabled
              placeholder={t("charge_plans.step1.code.placeholder")}
              className="h-11 w-full cursor-not-allowed rounded-lg border border-input bg-muted/40 px-3 font-mono text-sm uppercase text-muted-foreground placeholder:text-muted-foreground"
            />
          </Field>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          <ModalityCard
            checked={state.tipoValor === "fixo"}
            onClick={() => setState({ ...state, tipoValor: "fixo" })}
            icon="👤"
            title={t("charge_plans.step1.card_fixed.title")}
            desc={t("charge_plans.step1.card_fixed.desc")}
            tag={t("charge_plans.step1.card_fixed.tag")}
            tagTone="primary"
          />
          <ModalityCard
            checked={state.tipoValor === "escolhido_cliente"}
            onClick={() =>
              setState({ ...state, tipoValor: "escolhido_cliente" })
            }
            icon="👥"
            title={t("charge_plans.step1.card_client.title")}
            desc={t("charge_plans.step1.card_client.desc")}
            tag={t("charge_plans.step1.card_client.tag")}
            tagTone="success"
          />
        </div>

        <div className="rounded-xl border border-primary-100 bg-primary-50 p-4 text-sm text-primary-900">
          <span className="font-semibold">ℹ️ </span>
          {t("charge_plans.step1.callout")}
        </div>
      </div>

      <aside className="lg:sticky lg:top-4 lg:self-start">
        <ChargePlanMobilePreview state={state} />
      </aside>
    </div>
  );
}

function ModalityCard({
  checked,
  onClick,
  icon,
  title,
  desc,
  tag,
  tagTone,
}: {
  checked: boolean;
  onClick: () => void;
  icon: string;
  title: string;
  desc: string;
  tag: string;
  tagTone: "primary" | "success";
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`relative rounded-2xl border p-6 text-left transition ${
        checked
          ? "border-primary-500 bg-primary-50/40 shadow-sm"
          : "border-border bg-card hover:border-primary-200"
      }`}
    >
      <span
        className={`absolute right-4 top-4 flex h-6 w-6 items-center justify-center rounded-full border-2 ${
          checked ? "border-primary-600" : "border-input"
        }`}
      >
        {checked ? (
          <span className="h-3 w-3 rounded-full bg-primary-600" />
        ) : null}
      </span>
      <div className="mb-4 flex h-12 w-12 items-center justify-center rounded-xl bg-primary-50 text-2xl">
        {icon}
      </div>
      <h3 className="text-lg font-bold text-foreground">{title}</h3>
      <p className="mt-1 text-sm text-muted-foreground">{desc}</p>
      <span
        className={`mt-4 inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold ${
          tagTone === "primary"
            ? "bg-primary-100 text-primary-700"
            : "bg-emerald-100 text-emerald-700"
        }`}
      >
        ⭐ {tag}
      </span>
    </button>
  );
}
